/**
 * Maps an array of objects to a record using a specified field as the key.
 *
 * @template T - The type of objects in the array.
 * @template V - The type of values in the resulting record.
 * @param {T[]} array - The array of objects to map.
 * @param {string} idFieldName - The field name to use as the key for the record.
 * @param {(el: T) => V} [valueCallback=null] - Optional callback to determine the value for each key.
 * @returns {Record<string, V>} The resulting record with keys from the specified field and values from the callback or the original objects.
 */
export const mapToId = <T, V>(
    array: T[],
    idFieldName: string,
    valueCallback: ((el: T) => V) | null = null
): Record<string, V> => {
    return array.reduce((res: Record<string, V>, el: T) => {
        const key = String(el[idFieldName as keyof T]);
        res[key] = valueCallback ? valueCallback(el) : (el as unknown as V);
        return res;
    }, {});
};

// Generic function to sort an array of objects by a specific property, case-insensitively
export const sortByProperty = <T>(arr: T[], property: string, ascending: boolean = true): T[] => {
    return arr.toSorted((a, b) => {
        const valueA = a[property as keyof T] || a;
        const valueB = b[property as keyof T] || b;
        if (valueA == null && valueB == null) { return 0; }
        if (valueA == null) { return 1; }
        if (valueB == null) { return -1; }
        const strA = String(valueA).toLowerCase();
        const strB = String(valueB).toLowerCase();
        const cmp = strA.localeCompare(strB, undefined, { sensitivity: 'base' });
        return ascending ? cmp : -cmp;
    });
};
export const sortStrings = (arr: string[], ascending: boolean = true): string[] => {
    return [...arr].sort((a, b) => {
        const cmp = a.toLowerCase().localeCompare(b.toLowerCase(), undefined, { sensitivity: 'base' });
        return ascending ? cmp : -cmp;
    });
};
export const compareNullable = (value1: string | undefined, value2: string | undefined): number => {
    if (!value1 && !value2) {
        return 0;
    } else if (!value1) {
        return -1;
    } else if (!value2) {
        return 1;
    } else {
        return value1.localeCompare(value2);
    }
};
